import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface TourStep {
  /** CSS-selector waar de spotlight op landt. Als het element niet gevonden wordt,
   *  toont de stap zichzelf gecentreerd zonder highlight. */
  target?: string;
  title: string;
  content: string;
  /** Voorkeurspositie van de tooltip t.o.v. het target. Default 'bottom'. */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** Optioneel: callback voor extra state-setup vlak voor de stap toont
   *  (bv. een tab openen zodat het target zichtbaar is). */
  onEnter?: () => void;
}

const PADDING = 8;
const TOOLTIP_WIDTH = 340;
const TOOLTIP_MARGIN = 14;

/**
 * Herbruikbare rondleiding met spotlight-overlay en tooltip. Onthoudt via
 * localStorage of de gebruiker de tour al heeft gezien; kan opnieuw
 * gestart worden via het hulp-knopje rechtsonder.
 */
@Component({
  selector: 'app-onboarding-tour',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './onboarding-tour.component.html',
  styleUrls: ['./onboarding-tour.component.scss'],
})
export class OnboardingTourComponent implements OnInit, OnDestroy {
  @Input() storageKey = 'cockpit-onboarding:default';
  @Input() version = 1;
  @Input() reopenLabel = 'Rondleiding';
  @Input() steps: TourStep[] = [];

  open = false;
  stepIndex = 0;
  targetRect: { top: number; left: number; width: number; height: number } | null = null;
  tooltipStyle: Record<string, string> = { top: '40px', left: '40px' };

  private resizeHandler = () => this.recomputeRects();
  private scrollHandler = () => this.recomputeRects();
  private rafId: number | null = null;

  get currentStep(): TourStep | null {
    return this.steps[this.stepIndex] || null;
  }

  get storageKeyFull(): string {
    return `${this.storageKey}:v${this.version}`;
  }

  ngOnInit(): void {
    if (!this.steps.length) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(this.storageKeyFull) === '1'; } catch {}
    if (!dismissed) {
      // Wacht even zodat de editor-UI z'n eerste render heeft afgerond.
      setTimeout(() => this.start(), 250);
    }
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('scroll', this.scrollHandler);
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
  }

  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (!this.open) return;
    if (e.key === 'Escape') this.close(false);
    if (e.key === 'ArrowRight') this.next();
    if (e.key === 'ArrowLeft') this.prev();
  }

  start(): void {
    if (!this.steps.length) return;
    this.stepIndex = 0;
    this.open = true;
    this.enterStep(0);
  }

  reopen(): void { this.start(); }

  close(markCompleted: boolean): void {
    this.open = false;
    if (markCompleted) {
      try { localStorage.setItem(this.storageKeyFull, '1'); } catch {}
    }
  }

  next(): void {
    if (this.stepIndex >= this.steps.length - 1) { this.close(true); return; }
    this.stepIndex++;
    this.enterStep(this.stepIndex);
  }

  prev(): void {
    if (this.stepIndex <= 0) return;
    this.stepIndex--;
    this.enterStep(this.stepIndex);
  }

  private enterStep(index: number): void {
    const step = this.steps[index];
    if (!step) return;
    try { step.onEnter?.(); } catch { /* fouten in setup mogen tour niet blokkeren */ }
    // Twee rAF-ticks zodat DOM-changes (bv. tab-switch) klaar zijn met renderen.
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.rafId = requestAnimationFrame(() => this.recomputeRects());
    });
  }

  private recomputeRects(): void {
    const step = this.currentStep;
    if (!step) { this.targetRect = null; return; }
    if (!step.target) { this.targetRect = null; this.computeTooltipPosition(null, step); return; }

    const el = document.querySelector<HTMLElement>(step.target);
    if (!el) { this.targetRect = null; this.computeTooltipPosition(null, step); return; }

    // Scroll het target in beeld voor de eerste meting.
    const r = el.getBoundingClientRect();
    const inView = r.top >= 0 && r.bottom <= window.innerHeight;
    if (!inView) el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });

    const rect = el.getBoundingClientRect();
    this.targetRect = { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
    this.computeTooltipPosition(this.targetRect, step);
  }

  private computeTooltipPosition(
    target: { top: number; left: number; width: number; height: number } | null,
    step: TourStep
  ): void {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (!target) {
      // Center-scherm fallback
      this.tooltipStyle = {
        top: `${Math.max(80, vh / 2 - 140)}px`,
        left: `${Math.max(12, vw / 2 - TOOLTIP_WIDTH / 2)}px`,
        width: `${TOOLTIP_WIDTH}px`,
      };
      return;
    }

    const placement = step.placement ?? 'bottom';
    let top: number;
    let left: number;

    if (placement === 'right' && target.left + target.width + TOOLTIP_WIDTH + TOOLTIP_MARGIN < vw) {
      top = target.top + target.height / 2 - 80;
      left = target.left + target.width + TOOLTIP_MARGIN;
    } else if (placement === 'left' && target.left - TOOLTIP_WIDTH - TOOLTIP_MARGIN > 0) {
      top = target.top + target.height / 2 - 80;
      left = target.left - TOOLTIP_WIDTH - TOOLTIP_MARGIN;
    } else if (placement === 'top' && target.top - 240 > 0) {
      top = target.top - 240;
      left = target.left + target.width / 2 - TOOLTIP_WIDTH / 2;
    } else {
      // default: onder het target
      top = target.top + target.height + TOOLTIP_MARGIN;
      left = target.left + target.width / 2 - TOOLTIP_WIDTH / 2;
    }

    top = Math.max(12, Math.min(top, vh - 220));
    left = Math.max(12, Math.min(left, vw - TOOLTIP_WIDTH - 12));

    this.tooltipStyle = {
      top: `${top}px`,
      left: `${left}px`,
      width: `${TOOLTIP_WIDTH}px`,
    };
  }

  spotlightStyle(): Record<string, string> | null {
    if (!this.targetRect) return null;
    return {
      top: `${this.targetRect.top - PADDING}px`,
      left: `${this.targetRect.left - PADDING}px`,
      width: `${this.targetRect.width + PADDING * 2}px`,
      height: `${this.targetRect.height + PADDING * 2}px`,
    };
  }
}
